/**
 * Calendar data for the `/calendar` route (web/docs/screens.md §`/calendar`).
 * The primary feed is `GET /v1/calendar/mine` — the airing calendar scoped to
 * the user's full library (watching + watchlist), exactly what the mobile
 * `getMyCalendar` does (`app/src/data/api-real.ts`). A `404` (endpoint the
 * backend has not shipped) degrades to an empty calendar, the same contract as
 * the app.
 */

import { useQuery } from '@tanstack/react-query';

import { apiGet, ApiError } from '@/api/http';
import type { ApiCalendarEntry, ApiCalendarMineResponse } from '@shared/api-types';
import { imageUrls, paletteFromTitle } from '@shared/mappers';
import type { CalendarEntry, MyCalendar, UserShowStatus } from '@shared/types';

export const CALENDAR_QUERY_KEY = ['calendar', 'mine'] as const;

/**
 * Wire → UI mapping, shaped like the mobile mapper (which lives inline in
 * `api-real.ts` — there is nothing to import): palette is derived client-side,
 * poster keys go through the shared `imageUrls()` chain (w185 thumbs).
 */
export function mapCalendarEntry(r: ApiCalendarEntry): CalendarEntry {
  const poster = imageUrls(r.posterKey, r.posterPath, 'w185');
  return {
    titleId: r.titleId,
    title: r.title,
    titleBr: r.titleBr ?? undefined,
    palette: paletteFromTitle(r.title),
    seasonNumber: r.seasonNumber,
    episodeNumber: r.episodeNumber,
    episodeTitle: r.episodeTitle,
    episodeTitleBr: r.episodeTitleBr ?? undefined,
    airDate: r.airDate,
    posterUrl: poster.primary,
    posterFallbackUrl: poster.fallback,
  };
}

export async function fetchMyCalendar(): Promise<MyCalendar> {
  let res: ApiCalendarMineResponse;
  try {
    res = await apiGet<ApiCalendarMineResponse>('/v1/calendar/mine');
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) return { entries: [], library: {} };
    throw err;
  }
  return {
    entries: (res.entries ?? []).map(mapCalendarEntry),
    library: Object.fromEntries(
      Object.entries(res.library ?? {}).map(([id, v]) => [
        id,
        {
          status: (v.status as UserShowStatus) ?? 'watching',
          watchedCount: Math.round(v.watchedCount ?? 0),
        },
      ]),
    ),
  };
}

/**
 * The schedule. Data comes from the query cache (PageTransition re-serves
 * cached route elements, so the screen must not rely on a fresh mount — see
 * web/docs/data-layer.md).
 */
export function useMyCalendar() {
  return useQuery({ queryKey: CALENDAR_QUERY_KEY, queryFn: fetchMyCalendar });
}
